import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { apiRequest } from '@/utils/api-request';
import { Upload, Loader2, Trash2, Film, X } from 'lucide-react';

// Short videos from the PC or a Google Drive link. A PC file goes straight to
// DigitalOcean Spaces through a signed URL; the backend then compresses the video
// (or the Drive file) to 720p, puts it on the CDN, and on save links it to the
// chosen school's profile.

const MAX_BYTES = 500 * 1024 * 1024; // 500 MB before compression
const MAX_SECONDS = 180; // 3 minutes
const ACCEPTED = ['video/mp4', 'video/webm', 'video/quicktime'];
const VIDEO_TYPES = [
  { value: 'review', label: 'Review' },
  { value: 'interview', label: 'Interview' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'general', label: 'General' },
];

interface School {
  slate_id: number;
  slate_name: string;
  city_name: string;
  area_name: string;
}

interface UploadedVideo {
  video_id: number;
  title: string;
  video_url: string;
  thumbnail: string;
  duration: string;
  video_type: string;
  publishedAt: string;
  slate_id: number | null;
  slate_name: string | null;
  city: string | null;
  area: string | null;
}

interface UploadUrl {
  uploadUrl: string;
  key: string;
  acl: string;
}

interface ProcessingJob {
  status: 'queued' | 'downloading' | 'compressing' | 'uploading' | 'done' | 'error';
  progress: number;
  message: string;
}

const STEP_LABEL: Record<string, string> = {
  queued: 'Waiting',
  downloading: 'Downloading',
  compressing: 'Compressing',
  uploading: 'Saving to CDN',
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Reads only the duration of a local file, so a too-long video is rejected before uploading.
const readDuration = (file: File): Promise<number> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration || 0);
    };
    // Some formats (e.g. certain MOV) can't be read by the browser; the server checks again.
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
  });

// PUT the file to the signed URL. XHR (not fetch) so we can show upload progress.
const putFile = (url: string, file: Blob, contentType: string, acl: string, onProgress: (pct: number) => void) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('x-amz-acl', acl);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.send(file);
  });

const UploadVideos: React.FC = () => {
  const [mode, setMode] = useState<'file' | 'drive'>('file');
  const [driveUrl, setDriveUrl] = useState('');
  const [step, setStep] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoType, setVideoType] = useState('review');
  const [school, setSchool] = useState<School | null>(null);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolResults, setSchoolResults] = useState<School[]>([]);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videos, setVideos] = useState<UploadedVideo[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadVideos = async () => {
    setLoadingList(true);
    try {
      const res = await apiRequest<UploadedVideo[]>('/youtube-videos/uploads');
      if (res.success) setVideos(Array.isArray(res.data) ? res.data : []);
      else toast.error(res.message || 'Failed to load uploaded videos');
    } catch {
      toast.error('Failed to load uploaded videos');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // Debounced school search, same endpoint as the Link to Schools tab
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (school || schoolSearch.trim().length < 2) {
      setSchoolResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiRequest<{ schools: School[] }>('/schools/search', {
          method: 'POST',
          body: { search_term: schoolSearch.trim(), limit: 20 },
        });
        if (res.success) setSchoolResults(res.data?.schools || []);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [schoolSearch, school]);

  const pickFile = (f: File | undefined) => {
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error('Choose an MP4, WebM or MOV video');
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error('Video must be 500 MB or smaller');
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '));
  };

  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setTitle('');
    setDescription('');
    setVideoType('review');
    setSchool(null);
    setSchoolSearch('');
    setDriveUrl('');
    setStep('');
    setProgress(0);
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleUpload = async () => {
    if (mode === 'file' && !file) return toast.error('Choose a video first');
    if (mode === 'drive' && !/drive\.google\.com\//.test(driveUrl)) return toast.error('Paste a Google Drive file link');
    if (!school) return toast.error('Select the school this video belongs to');
    if (!title.trim()) return toast.error('Title is required');

    setUploading(true);
    setProgress(0);
    try {
      let body: Record<string, string>;
      if (mode === 'file' && file) {
        const seconds = await readDuration(file);
        if (seconds > MAX_SECONDS + 0.5) throw new Error('Video must be 3 minutes or shorter');

        const link = await apiRequest<UploadUrl>('/youtube-videos/upload-url', {
          method: 'POST',
          body: { contentType: file.type, size: file.size },
        });
        if (!link.success || !link.data) throw new Error(link.message || 'Could not start the upload');
        setStep('Uploading');
        await putFile(link.data.uploadUrl, file, file.type, link.data.acl, setProgress);
        body = { key: link.data.key };
      } else {
        body = { drive_url: driveUrl.trim() };
      }

      // The server compresses to 720p and puts the result on the CDN; poll until it finishes.
      const started = await apiRequest<{ job_id: string }>('/youtube-videos/process', { method: 'POST', body });
      if (!started.success || !started.data) throw new Error(started.message || 'Could not start processing');
      const jobId = started.data.job_id;
      for (;;) {
        await sleep(2000);
        const res = await apiRequest<ProcessingJob>(`/youtube-videos/process/${jobId}`);
        if (!res.success || !res.data) throw new Error(res.message || 'Lost track of processing, please try again');
        const job = res.data;
        if (job.status === 'error') throw new Error(job.message);
        if (job.status === 'done') break;
        setStep(STEP_LABEL[job.status] || job.message);
        setProgress(job.progress);
      }

      setStep('Saving');
      const saved = await apiRequest('/youtube-videos/uploads', {
        method: 'POST',
        body: {
          job_id: jobId,
          title: title.trim(),
          description: description.trim(),
          video_type: videoType,
          slate_id: school.slate_id,
        },
      });
      if (!saved.success) throw new Error(saved.message || 'Could not save the video');

      toast.success(`Video uploaded and linked to ${school.slate_name}`);
      reset();
      loadVideos();
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setStep('');
    }
  };

  const handleDelete = async (v: UploadedVideo) => {
    if (!window.confirm(`Delete "${v.title}"? It will be removed from the videos page and the school profile.`)) return;
    setDeletingId(v.video_id);
    try {
      const res = await apiRequest(`/youtube-videos/uploads/${v.video_id}`, { method: 'DELETE' });
      if (!res.success) throw new Error(res.message || 'Delete failed');
      toast.success('Video deleted');
      setVideos((prev) => prev.filter((x) => x.video_id !== v.video_id));
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto px-8 py-6 space-y-8">
      {/* Upload form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Upload a short video</h2>
        <p className="text-sm text-gray-500 mb-5">From your computer (MP4, WebM or MOV, up to 500 MB) or a Google Drive link · up to 3 minutes. We compress it to 720p and show it on the videos page and the school's profile.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Source: computer file or Google Drive link */}
          <div>
            <div className="inline-flex border border-gray-200 rounded-md overflow-hidden mb-3 text-sm">
              {(['file', 'drive'] as const).map((m) => (
                <button
                  key={m}
                  disabled={uploading}
                  onClick={() => setMode(m)}
                  className={`px-3 py-1.5 ${mode === m ? 'bg-brandColor text-white' : 'bg-white text-gray-600 hover:bg-gray-50'} ${m === 'drive' ? 'border-l border-gray-200' : ''}`}
                >
                  {m === 'file' ? 'From computer' : 'Google Drive link'}
                </button>
              ))}
            </div>
            {mode === 'drive' ? (
              <div>
                <input
                  value={driveUrl}
                  onChange={(e) => setDriveUrl(e.target.value)}
                  disabled={uploading}
                  placeholder="https://drive.google.com/file/d/…/view"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">The file must be shared as "Anyone with the link can view". Up to 500 MB.</p>
              </div>
            ) : previewUrl ? (
              <div className="relative">
                <video src={previewUrl} controls className="w-full max-h-80 rounded-md bg-black" />
                {!uploading && (
                  <button onClick={reset} className="absolute top-2 right-2 bg-white/90 rounded-full p-1 hover:bg-white" title="Remove">
                    <X className="h-4 w-4" />
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-2">{file?.name} · {((file?.size || 0) / 1024 / 1024).toFixed(1)} MB</p>
              </div>
            ) : (
              <button
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pickFile(e.dataTransfer.files?.[0]);
                }}
                className="w-full h-56 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center text-gray-500 hover:border-brandColor hover:text-brandColor"
              >
                <Film className="h-8 w-8 mb-2" />
                <span className="text-sm font-medium">Choose a video or drop it here</span>
              </button>
            )}
            <input ref={fileInput} type="file" accept={ACCEPTED.join(',')} className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">School *</label>
              {school ? (
                <div className="flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 text-sm">
                  <span>
                    {school.slate_name}
                    <span className="text-gray-500"> · {[school.area_name, school.city_name].filter(Boolean).join(', ')}</span>
                  </span>
                  {!uploading && (
                    <button onClick={() => setSchool(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                  )}
                </div>
              ) : (
                <>
                  <input
                    value={schoolSearch}
                    onChange={(e) => setSchoolSearch(e.target.value)}
                    placeholder="Search school by name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                  {searching && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-9 text-gray-400" />}
                  {schoolResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {schoolResults.map((s) => (
                        <button
                          key={s.slate_id}
                          onClick={() => {
                            setSchool(s);
                            setSchoolResults([]);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          {s.slate_name}
                          <span className="text-gray-500"> · {[s.area_name, s.city_name].filter(Boolean).join(', ')}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={videoType} onChange={(e) => setVideoType(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                {VIDEO_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || (mode === 'file' ? !file : !driveUrl.trim())}
              className="bg-brandColor text-white px-4 py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {step ? `${step}${step === 'Saving' || step === 'Waiting' ? '…' : ` ${progress}%`}` : 'Starting…'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload video
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Uploaded videos */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Uploaded videos ({videos.length})</h2>
        </div>
        {loadingList ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : videos.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No uploaded videos yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {videos.map((v) => (
              <div key={v.video_id} className="flex items-center gap-4 px-6 py-3">
                <a href={v.video_url} target="_blank" rel="noreferrer" className="shrink-0">
                  <img src={v.thumbnail} alt="" className="h-16 w-28 object-cover rounded bg-gray-100" />
                </a>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{v.title}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {v.slate_name || 'No school'}
                    {v.area || v.city ? ` · ${[v.area, v.city].filter(Boolean).join(', ')}` : ''}
                    {' · '}{v.video_type} · {v.duration} · {new Date(v.publishedAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(v)}
                  disabled={deletingId === v.video_id}
                  className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                  title="Delete"
                >
                  {deletingId === v.video_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadVideos;
